import axios from 'axios'

const origin = process.env.VUE_APP_API_ORIGIN
const responseData = response => response.data

/**
 * 月次の請求（運営が見る側）。
 *
 * **口は運営の道（/access-management）に置いてある。**
 * 独自の道に置くと、経路表へ載せ忘れたときに誰でも通ってしまう。
 * 運営の道は「知らない経路を断る」側なので、閉じる向きに倒れる。
 *
 * 参照は PROVIDER_BILLING_VIEW、発行は PROVIDER_INVOICE_ISSUE。
 * **発行は番号を採る。採った番号は取り消せない。**
 */
export default {
  /**
   * プランの一覧と、今月の請求に使う料金（今月の1日時点の記録）。
   *
   * **設定する場所が無いと全部 0 のまま。**請求額が常に 0 円になる。
   * 実機で実際に 0 円と出た（2026-09-05）。
   *
   * 各行に currentAppliedOn（今の料金の記録の日）と、月ごとの料金 priceSchedule
   * （[{ appliedOn: その月の1日, basePrice, pricePerUser, minimumMonthlyAmount }]、月の順。
   * 先の月も過去の月も）・月ごとの組 volumeDiscountSchedule（[{ appliedOn, tiers }]）が入る
   * （2026-09-30 の見直し。「プランの料金」の欄を選んでいる適用請求月の値にするため）。
   * 契約企業一覧のプランマスターとプランの選択肢も、この一覧を読む（今月の料金だけを使う）。
   */
  plans () {
    return axios
      .get(origin + '/access-management/billing/plans')
      .then(responseData)
  },

  /**
   * 発行元（運営会社）の設定。
   *
   * **請求書に載る内容。**住所も登録番号も空のままだと請求書を
   * 発行できない（採った番号は取り消せないので、出す前に断る）。
   * 角印そのものは返らない。登録されているかどうかだけ。
   *
   * **月初の自動確定が動いているか（autoFixEnabled）も入る**（2026-09-30）。
   * 画面は［この月を確定する］の説明をこれで出し分ける。保存（saveIssuer）の
   * 応答にも入る。新しい口は作っていない。
   */
  issuer () {
    return axios
      .get(origin + '/access-management/billing/issuer')
      .then(responseData)
  },

  /**
   * 確認が必要な請求。
   *
   * **確定した時点の支払方法が分からない請求。**その請求は督促を
   * 保留している（いまの設定で代用すると、確定後に支払方法を
   * 変えていた会社の扱いが変わるため）。運営が見て判断する。
   */
  reviewNeeded () {
    return axios
      .get(origin + '/access-management/billing/review-needed')
      .then(responseData)
  },

  /**
   * 間違えて出した請求を取り消す。
   *
   * **記録は消さない。**取り消したことと理由が残る。取り消した
   * あとは、その月をもう一度確定できる。
   */
  voidBilling (billingPeriodId, reason) {
    return axios
      .post(origin + '/access-management/billing/periods/void', {
        billingPeriodId,
        reason
      })
      .then(responseData)
  },

  /**
   * 発行元の設定を保存する。
   *
   * **POST。**運営の経路は GET と POST しか通さない。PUT で書くと
   * 権限の判定に届く前に断られる。
   *
   * sealImage は base64。**渡さなければ今の角印をそのまま残す。**
   * 空文字を渡すと消える。
   */
  saveIssuer (form) {
    return axios
      .post(origin + '/access-management/billing/issuer', form)
      .then(responseData)
  },

  /**
   * 契約の状態が変わった記録。**直近100件。**
   *
   * 「なぜこの会社が止まっているのか」に答えるためのもの。
   * **記録は前から取っていたのに、読む経路が1本も無かった。**
   */
  contractHistory () {
    return axios
      .get(origin + '/access-management/billing/contract-history')
      .then(responseData)
  },

  /**
   * プランの料金を保存する。
   *
   * **確定済みの請求は動かない。**確定した時点の単価を写し取って
   * あるので、次の確定から効く。
   */
  /**
   * @param appliedOn 適用請求月の1日（yyyy-MM-01）。その月の請求から効く（2026-09-30 に日付から
   *   月へ変えた。back は月の1日だけを受ける）。先の月も、料金が無くて確定できなかった過去の月も書ける。
   * @return { appliedOn, laterAppliedOn, fixedBillingExists }。laterAppliedOn は選んだ月より後で
   *   最初の料金の記録の日（無ければ null）、fixedBillingExists はその月から後にこのプランの
   *   確定済みの請求があるか。画面はこれで保存の結果の文を出し分ける。
   * @param minimumMonthlyAmount 最低月額（円）。**null は「最低月額なし」。**
   *   料金と同じ記録（適用請求月つき）に入る。**この口は鍵をいつも送る**
   *   （渡さなければ null＝なし）ので、今の値を変えないときも今の値をそのまま渡す。
   *   back は鍵そのものが無い保存（最低月額の欄が無い古い画面）だけを「変えない」で受ける。
   */
  savePlanPricing (planId, basePrice, pricePerUser, appliedOn, minimumMonthlyAmount) {
    return axios
      .post(origin + '/access-management/billing/plans/pricing', {
        planId,
        basePrice,
        pricePerUser,
        appliedOn,
        minimumMonthlyAmount: minimumMonthlyAmount === undefined ? null : minimumMonthlyAmount
      })
      .then(responseData)
  },

  /**
   * プランのボリュームディスカウント（人数帯ごとの割引率）を保存する。PLAN_CHANGE。
   *
   * 料金と同じく**適用請求月つき**の記録で、同じ月の組は置き換わる。
   * 「割引なし」は 1名から0% の1行で送る（帯を消したいときも同じ）。
   * 帯の単価は持たない（請求の計算のとき、その月の定価から出す）。
   * 一覧（plans）の各プランには volumeDiscount（今月の組 { appliedOn, tiers } か null）と
   * volumeDiscountSchedule（月ごとの組。appliedOn はその組を使い始める月の1日）が返る。
   *
   * @param appliedOn 適用請求月の1日（yyyy-MM-01）。先の月も書ける。
   * @param tiers [{ fromSeat, discountRate }]（数。人数の昇順）
   * @return 料金の保存と同じ形 { appliedOn, laterAppliedOn（その月より後で最初の組の日。無ければ null）,
   *   fixedBillingExists（その月から後にこのプランの確定済みの請求があるか） }
   */
  savePlanVolumeDiscount (planId, appliedOn, tiers) {
    return axios
      .post(origin + '/access-management/billing/plans/volume-discount', {
        planId,
        appliedOn,
        tiers
      })
      .then(responseData)
  },

  /**
   * プランそのものを増やす／名前と並びを直す。
   *
   * **料金はここでは変えられない。**料金を変える道は
   * savePlanPricing だけで、そちらは適用開始日つきで履歴を1本残す。
   * ここに料金を混ぜると、履歴に残らないまま単価が変わり、
   * 対象月の請求根拠を復元できなくなる。
   *
   * @param form planId が無ければ新規。新規のときは
   *   planCode / planName / displayOrder / basePrice / pricePerUser /
   *   copyFeaturesFromPlanId が要る。minimumMonthlyAmount（最低月額、
   *   null＝なし）も新規のときだけ送る（最初の料金の記録に入る）。
   *   volumeDiscountTiers（人数割の帯 [{ fromSeat, discountRate }]、数・人数の昇順。null＝割引なし）も
   *   新規のときだけ送る（［プランを追加］の「人数割の帯」。0108）。
   *   **料金と写し元は後から足せない。**料金が無いプランは請求が
   *   立たず、機能が0本のプランは付けた会社が何もできなくなる。
   */
  savePlan (form) {
    return axios
      .post(origin + '/access-management/billing/plans', form)
      .then(responseData)
  },

  /**
   * その月の請求を、いま確定させる。
   *
   * **ふだんは毎月1日の 03:10 に自動で走る。**それが落ちた月に、
   * 次の月初まで請求書を1枚も出せなくなるので口がある。
   * 何度押しても増えない（同じ月に2本作らない）。
   */
  /**
   * その月の請求を確定する。
   *
   * accountId を渡すとその会社だけ。**席数の記録が足りなくて
   * 見送った会社を、登録したあとに確定し直すときに使う。**
   */
  fixPeriods (yearMonth, accountId) {
    return axios
      .post(origin + '/access-management/billing/periods/fix', {
        yearMonth,
        accountId: accountId === undefined || accountId === null
          ? '' : String(accountId)
      })
      .then(responseData)
  },

  /**
   * 請求書のPDFを開く。
   *
   * **新しいタブで開く。**axios で取ると Blob を作ってから
   * ダウンロードさせることになり、ファイル名がサーバの指定と
   * ずれる。ブラウザにそのまま任せる。
   */
  invoicePdfUrl (billingPeriodId) {
    return origin + '/access-management/billing/invoices/pdf'
      + '?billingPeriodId=' + encodeURIComponent(billingPeriodId)
  },

  /**
   * 支払状況を、いま見直す。
   *
   * **ふだんは毎日 3:20 に自動で走る。**入金を記録した直後に
   * 「これで戻りますか」と聞かれたときのための口。
   * 何度押しても同じ結果になる。
   */
  /**
   * 支払状況を見直す。
   *
   * accountId を渡すとその会社だけ。**入金は保存できたが見直しに
   * 失敗した、というときに入金を二重に登録せず再試行するため。**
   */
  runDunning (accountId) {
    return axios
      .post(origin + '/access-management/billing/dunning/run',
        accountId === undefined || accountId === null
          ? {} : { accountId: String(accountId) })
      .then(responseData)
  },

  /**
   * その請求に対する入金の明細。
   *
   * **一部だけ振り込まれることがある**（振込手数料を引かれた、
   * 複数月をまとめて払った）。いくら足りないのかを、その場で見せる。
   */
  payments (billingPeriodId) {
    return axios
      .get(origin + '/access-management/billing/payments', {
        params: { billingPeriodId }
      })
      .then(responseData)
  },

  /**
   * 入金を1件記録する。
   *
   * **銀行振込は、入金があったことをシステムが知る手段が無い。**
   * 通帳を見た人が記録するしかない。
   *
   * **記録した直後に、back がその会社だけ支払状況を見直す**（合計が請求額に
   * 届けば札が「入金済み」になり、止まっていた契約が戻る）。全社の見直しは
   * 毎日 03:20 の督促と［支払状況を見直す］。
   */
  recordPayment (form) {
    return axios
      .post(origin + '/access-management/billing/payments', form)
      .then(responseData)
  },

  /** 入金の記録を取り消す（打ち間違い）。 */
  cancelPayment (paymentId) {
    return axios
      .post(origin + '/access-management/billing/payments/cancel', {
        paymentId
      })
      .then(responseData)
  },

  /** その月の請求一覧。会社ごとの金額・入金・発行状況。 */
  periods (yearMonth) {
    return axios
      .get(origin + '/access-management/billing/periods', {
        params: { yearMonth }
      })
      .then(responseData)
  },

  /**
   * いま在籍している人を、当月の請求人数へ数え入れる。
   *
   * **押した日（今日）の分だけを記録する。**ふだんは毎日 2:50 に自動で
   * 数える。その処理が落ちた日の手当てには、その日のうちに押す。
   * 過ぎた日の分は記録できない（back は今日の日付で数える）。
   *
   * **何度押しても増えない。**同じ月の同じ人は1行だけ。
   */
  countHeadcount () {
    return axios
      .post(origin + '/access-management/billing/headcount/count', {})
      .then(responseData)
  },

  /**
   * 入金・売掛管理（会社 × 月の未収）。
   *
   * **請求管理はその月だけを見る画面。**古い月のぶんが残ったまま
   * 新しい月だけ払われている、という状態は月を1つずつ切り替えても
   * 見つけられない。ここは会社ごとに横へ並べる。
   *
   * @param {number} months    何か月ぶん並べるか（省略すると12か月）
   * @param {number} accountId 1社だけ見るとき（契約企業一覧の
   *                           「請求・入金」タブ。省略すると全社）
   */
  receivables (months, accountId) {
    return axios
      .get(origin + '/access-management/billing/receivables', {
        params: { months, accountId }
      })
      .then(responseData)
  },

  /**
   * その月の確定ぶんを、まとめて発行する。
   *
   * **1社ずつ結果が返る。**件数だけだと、どれが出てどれが飛んだのか
   * 分からない。
   */
  issueAll (yearMonth) {
    return axios
      .post(origin + '/access-management/billing/invoices/issue-all', {
        yearMonth
      })
      .then(responseData)
  },

  /**
   * 選んだぶんだけ発行する。
   *
   * **1件だけ渡せば個別発行。**個別のために口を増やしていない。
   */
  issueSelected (billingPeriodIds) {
    return axios
      .post(origin + '/access-management/billing/invoices/issue-selected', {
        billingPeriodIds
      })
      .then(responseData)
  },

  /* ------------------------------------------------------------------
     請求書メールの送付

     請求書を発行すると、送信予約が1本積まれる（発行と同じ取引）。
     決めた日時が来ると送られる。**運営が止めなければ届く。**
     ------------------------------------------------------------------ */

  /** 送付設定（運営の1行）。 */
  mailSetting () {
    return axios
      .get(origin + '/access-management/billing/mail/setting')
      .then(responseData)
  },

  /**
   * 送付設定を保存する。
   *
   * **保存した結果がそのまま返る。**画面は読み直さなくてよい。
   */
  saveMailSetting (form) {
    return axios
      .post(origin + '/access-management/billing/mail/setting', form)
      .then(responseData)
  },

  /** 送信予定と、送った履歴。月を渡さなければ全部。 */
  mailOutbox (yearMonth) {
    return axios
      .get(origin + '/access-management/billing/mail/outbox', {
        params: { yearMonth }
      })
      .then(responseData)
  },

  /**
   * 予約が積まれていない発行済み請求書。
   *
   * **この仕組みを入れる前に発行したものが残る。**
   * 0件でないことを画面に出さないと、送られないまま誰も気づかない。
   */
  mailMissing (yearMonth) {
    return axios
      .get(origin + '/access-management/billing/mail/missing', {
        params: { yearMonth }
      })
      .then(responseData)
  },

  /**
   * この予約を止める。
   *
   * **止められるのは送信待ちだけ。**もう送りにいっているものは
   * 止められない（409で断られる）。
   */
  holdMail (id) {
    return axios
      .post(origin + '/access-management/billing/mail/outbox/hold', { id })
      .then(responseData)
  },

  /** 止めた予約を、送信待ちへ戻す。 */
  releaseMail (id) {
    return axios
      .post(origin + '/access-management/billing/mail/outbox/release',
        { id })
      .then(responseData)
  },

  /**
   * 宛先を直したので、積み直す。
   *
   * **「戻す」では駄目。**あれは状態を送信待ちへ戻すだけで、
   * 宛先も予定日時も更新しないので、空のまま拾われてまた落ちる。
   */
  requeueMail (id) {
    return axios
      .post(origin + '/access-management/billing/mail/outbox/requeue',
        { id })
      .then(responseData)
  },

  /**
   * 時刻が来ているぶんを、いま送る。
   *
   * **ふだんは毎時05分に自動で走る。**止めたものを戻した直後に
   * 「これで届きますか」と聞かれたときのための口。
   */
  runMail () {
    return axios
      .post(origin + '/access-management/billing/mail/run', {})
      .then(responseData)
  }
}
